import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Box } from '@mui/system';
import { useDispatch } from 'react-redux';
import { ExcelRenderer } from 'react-excel-renderer';
import { BiCloudDownload, BiCloudUpload } from 'react-icons/bi';
import { pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router-dom';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import { fDate } from '../../utils/formatTime';
import Loader from '../Global/Loader';
import { DateRangeStyled } from '../Global/Styling';
import RevenueService from '../services/RevenueService';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getBankDropdownData, getMasterListType } from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const { UploadMisFile, UploadRevenueFile, DownloadMisTemplate } = RevenueService;

function UploadBulkFile() {
  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploadfile, setUploadfile] = useState(null);
  const [openLoader, setOpenLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const [validationMsg, setValidationMsg] = useState('');
  const [downloadTemplateMsg, setDownloadTemplateMsg] = useState('');
  const [openDownloadTemplateDialog, setOpenDownloadTemplateDialog] = useState(false);

  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);

  const [fileDetails, setFileDetails] = useState({
    BankName: null,
    Source: '',
    SourceDate: '',
    FileType: '', // will store MasterTypeListDrop.value (128 / 130 / 132)
    period: {
      startDate: null,
      endDate: null
    }
  });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { BankDrop, MasterTypeListDrop } = useGlobalSelector();

  const getBankDropApi = useCallback(() => {
    if (!LoginData) return;
    const data = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getBankDropdownData(data));
  }, [LoginData, dispatch]);

  // Master Type List
  const getMasterTypeListApi = useCallback(() => {
    const type = 'REVENUE_FILE_STATUS_TYPE';
    dispatch(getMasterListType(type));
  }, [dispatch]);

  const handleRenderFile = (fileObj) => {
    ExcelRenderer(fileObj, (err, resp) => {
      if (err) {
        console.error('Error reading excel file', err);
        setOpenLoader(false);
        return;
      }
      // If you need to process `resp.rows`, do it here…
      setOpenLoader(false);
    });
  };

  // SIMPLE, SAFE FILE HANDLER
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name;
    const ext = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();

    setSelectedFileName(fileName);
    setUploadfile(file);

    if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
      setOpenLoader(true);
      handleRenderFile(file);
    } else {
      // reset file if wrong type
      setUploadfile(null);
      setSelectedFileName('');
      setOpen(true);
      setValidationMsg('Please add only xlsx/csv/xls file only');
    }
  };
  // console.log('Selected bank object:', fileDetails.BankName);
  // console.log('sss uploadfile:', uploadfile);
  // console.log('sss uploadfile size:', uploadfile?.size);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const bankId = fileDetails.BankName?.value;
    const revenueFileTypeId = fileDetails.FileType;

    if (!bankId) {
      setOpen(true);
      setValidationMsg('Please select bank name');
      return;
    }
    if (!revenueFileTypeId) {
      setOpen(true);
      setValidationMsg('Please select file type');
      return;
    }
    if (!uploadfile) {
      setOpen(true);
      setValidationMsg('Please upload MIS file');
      return;
    }

    if (uploadfile && uploadfile.size > 0) {
      setOpenLoader(true);

      const formUploadData = new FormData();
      formUploadData.append('bankId', bankId);
      formUploadData.append('companyId', LoginData?.companyId);
      formUploadData.append('misFile', uploadfile);
      formUploadData.append(
        'periodFromDate',
        filterDatesRange[0].startDate ? formatDate(filterDatesRange[0].startDate) : ''
      );
      formUploadData.append(
        'periodToDate',
        filterDatesRange[0].endDate ? formatDate(filterDatesRange[0].endDate) : ''
      );
      formUploadData.append('revenueFileTypeId', revenueFileTypeId);
      formUploadData.append(
        'sourceDate',
        fileDetails.SourceDate !== '' ? fileDetails.SourceDate : '1970-01-01'
      );
      formUploadData.append('sourceName', fileDetails.Source !== '' ? fileDetails.Source : null);
      formUploadData.append('userId', LoginData?.userId);

      fetch(UploadMisFile(), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${LoginData?.jwt}`
        },
        body: formUploadData
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to upload file.');
          }
          return response.json();
        })
        .then((data) => {
          if (data.status === 'success') {
            navigate('/revenue');
          } else {
            console.error('Upload failed:', data.message);
            setOpen(true);
            setValidationMsg(data.message || 'Upload failed');
          }
        })
        .catch((error) => {
          console.error('Error uploading file:', error);
          setOpen(true);
          setValidationMsg('Error uploading file');
        })
        .finally(() => {
          setOpenLoader(false);
        });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const DownloadMisTemplateApiResponse = await DownloadMisTemplate(LoginData?.jwt);
      const blob = await DownloadMisTemplateApiResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'revenue-mis-file-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadTemplateMsg('Template Successfully Downloaded');
      setOpenDownloadTemplateDialog(true);
    } catch (error) {
      console.error('Error downloading template:', error);
      setDownloadTemplateMsg('Error downloading template');
      setOpenDownloadTemplateDialog(true);
    }
  };

  const handleBankNameChange = (newValue) => {
    setFileDetails((prevData) => ({
      ...prevData,
      BankName: newValue
    }));
  };

  useEffect(() => {
    getBankDropApi();
    getMasterTypeListApi();

    // Optional: restore file type from localStorage if previously saved
    const stored = localStorage.getItem('Revenue');
    if (stored) {
      try {
        const authRevenue = JSON.parse(stored);
        setFileDetails((prev) => ({
          ...prev,
          FileType:
            typeof authRevenue === 'number' ? authRevenue : authRevenue?.value || prev.FileType
        }));
      } catch (err) {
        console.error('Error parsing Revenue from localStorage', err);
      }
    }
  }, [getBankDropApi, getMasterTypeListApi]);

  return (
    <Page title="Partner Management System || Revenue">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="Upload Files" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            File Details
          </Typography>

          <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
            <Grid container spacing={3} mb={3}>
              {/* Bank Name */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Bank Name
                  </Typography>
                  <Autocomplete
                    disablePortal
                    id="combo-box-bank"
                    options={BankDrop || []}
                    size="small"
                    getOptionLabel={(option) => option?.label || ''}
                    renderOption={(props, option) => <li {...props}>{option?.label}</li>}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth placeholder="Select Bank" />
                    )}
                    value={fileDetails.BankName || null}
                    onChange={(e, newValue) => handleBankNameChange(newValue)}
                  />
                </Box>
              </Grid>

              {/* Source */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Source
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{
                      className: 'textInput'
                    }}
                    placeholder="Type Source"
                    value={fileDetails.Source}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFileDetails((prevData) => ({
                        ...prevData,
                        Source: value
                      }));
                    }}
                  />
                </Box>
              </Grid>

              {/* Source Date */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Source Date
                  </Typography>
                  <TextField
                    fullWidth
                    inputProps={{
                      className: `textInput dissabledMenu${fileDetails.SourceDate}`
                    }}
                    placeholder="Select Source Date"
                    type="date"
                    value={fileDetails.SourceDate}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFileDetails((prevData) => ({
                        ...prevData,
                        SourceDate: value
                      }));
                    }}
                  />
                </Box>
              </Grid>

              {/* File Type */}
              <Grid item xs={4}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    File Type
                  </Typography>
                  <Select
                    fullWidth
                    inputProps={{
                      className: `textInput customSelectBox dissabledMenu${fileDetails.FileType}`
                    }}
                    value={fileDetails.FileType || ''}
                    onChange={(e) => {
                      const value = e.target.value; // 128 / 130 / 132
                      setFileDetails((prevData) => ({
                        ...prevData,
                        FileType: value
                      }));
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select File Type
                    </MenuItem>
                    {MasterTypeListDrop.length > 0 &&
                      MasterTypeListDrop.map((item) => (
                        <MenuItem value={item.value} key={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                  </Select>
                </Box>
              </Grid>

              {/* Period */}
              <Grid item xs={4} sx={{ position: 'relative' }}>
                <Box>
                  <Typography variant="h6" className="formLabel">
                    Period
                  </Typography>
                  <Box className="displayDateRange">
                    {filterDatesRange[0].startDate !== null
                      ? `${fDate(filterDatesRange[0].startDate)} to `
                      : ''}
                    {filterDatesRange[0].endDate !== null ? fDate(filterDatesRange[0].endDate) : ''}
                    {filterDatesRange[0].startDate === null &&
                    filterDatesRange[0].endDate === null ? (
                      <Typography className="dissabledMenu">Select Date Range</Typography>
                    ) : (
                      ''
                    )}
                  </Box>
                  <Select
                    fullWidth
                    inputProps={{
                      className: 'textInput'
                    }}
                    MenuProps={{
                      className: 'menuOpened'
                    }}
                    value=" "
                    className="datesSelect"
                  >
                    <DateRangeStyled
                      editableDateInputs={false}
                      onChange={(item) => setFilterDatesRange([item.selection])}
                      moveRangeOnFirstSelection={false}
                      ranges={filterDatesRange}
                      className="dateRangePicker"
                    />
                  </Select>
                </Box>
              </Grid>
            </Grid>

            {/* File upload + template download */}
            <Grid container alignItems="center" spacing={3}>
              <Grid item xs mb={3}>
                <Box variant="Container" className="uploadBox uploadBoxSec">
                  <input
                    accept=".xlsx, .xls, .csv"
                    className="fileInput"
                    id="contained-button-file"
                    multiple
                    type="file"
                    onChange={handleFileChange}
                  />

                  <label htmlFor="contained-button-file">
                    <Box>
                      {selectedFileName ? (
                        <Typography variant="body2">
                          {`Selected file name : ${selectedFileName}`}
                        </Typography>
                      ) : (
                        <>
                          <BiCloudUpload className="upladIcon" />
                          <Typography variant="h5">Upload</Typography>
                          <Typography variant="body2">
                            Supported file types: .xls, .xlsx, .csv
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
              </Grid>

              <Grid xs="auto" item mb={3} sx={{ flexGrow: '1' }}>
                <Button
                  className="mainBtn downloadBtn downloadBtnSec otherBtn"
                  variant="contained"
                  onClick={handleDownloadTemplate}
                >
                  <BiCloudDownload className="downloadIcon" />
                  <Typography variant="subtitle1">Download Template</Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>

          {/* Submit */}
          <Box textAlign="right">
            <Button variant="contained" className="mainBtn" onClick={handleSubmit}>
              Submit
            </Button>
          </Box>

          {/* Validation Dialog */}
          <Dialog
            open={open}
            onClose={() => setOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">{validationMsg}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Retry</Button>
            </DialogActions>
          </Dialog>

          {/* Download Template Dialog */}
          <Dialog
            open={openDownloadTemplateDialog}
            onClose={() => setOpenDownloadTemplateDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {downloadTemplateMsg}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDownloadTemplateDialog(false)}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* Loader */}
          <Loader openLoader={openLoader} />
        </Box>
      </Container>
    </Page>
  );
}

export default UploadBulkFile;

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  let month = `${d.getMonth() + 1}`;
  let day = `${d.getDate()}`;
  const year = d.getFullYear();

  if (month.length < 2) month = `0${month}`;
  if (day.length < 2) day = `0${day}`;

  return [year, month, day].join('-');
}
